import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
    Trophy, Star, TrendingUp, Users, Target, Award,
    DollarSign, Calendar, BarChart3, Gift, Calculator, Settings
} from 'lucide-react';

const EmployeePerformancePage = () => {
    const { api, user, hasPermission } = useAuth();
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [performances, setPerformances] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [bonusSettings, setBonusSettings] = useState({
        sales_bonus_rate: 2,  // % of sales
        attendance_bonus: 10000,  // Fixed amount for perfect attendance
        performance_threshold: 80  // Minimum score for bonus
    });
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    
    const canManageBonus = hasPermission?.('manage_bonus') || (user?.role === 'ADMIN');
    const canViewPerformance = hasPermission?.('view_performance') || 
        ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
    
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [employeesRes, performancesRes] = await Promise.all([
                api().get('/users'),
                api().get(`/pos/performance/employees?period=${selectedPeriod}`)
            ]);
            setEmployees(employeesRes.data);
            setPerformances(performancesRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [api, selectedPeriod]);
    
    useEffect(() => {
        if (canViewPerformance) {
            fetchData();
        }
    }, [fetchData, canViewPerformance]);
    
    const calculateBonus = (perf) => {
        let bonus = 0;
        
        // Sales bonus
        if (perf.total_sales > 0) {
            bonus += (perf.total_sales * bonusSettings.sales_bonus_rate / 100);
        }
        
        // Attendance bonus
        if (perf.attendance_rate >= 100) {
            bonus += bonusSettings.attendance_bonus;
        }
        
        // Only apply if performance score meets threshold
        if (perf.performance_score < bonusSettings.performance_threshold) {
            bonus = bonus * (perf.performance_score / 100);
        }
        
        return Math.round(bonus);
    };
    
    const getPerformanceColor = (score) => {
        if (score >= 90) return 'text-green-600';
        if (score >= 70) return 'text-amber-600';
        return 'text-red-600';
    };
    
    const getPerformanceBadge = (score) => {
        if (score >= 90) return { label: 'Excellent', variant: 'default', color: 'bg-green-500' };
        if (score >= 70) return { label: 'Bon', variant: 'secondary', color: 'bg-amber-500' };
        if (score >= 50) return { label: 'Moyen', variant: 'outline', color: 'bg-orange-500' };
        return { label: 'À améliorer', variant: 'destructive', color: 'bg-red-500' };
    };
    
    if (!canViewPerformance) {
        return (
            <div className="p-6 flex items-center justify-center h-64">
                <p className="text-muted-foreground">Vous n'avez pas accès à cette page</p>
            </div>
        );
    }
    
    return (
        <div className="p-6 space-y-6" data-testid="performance-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Trophy className="w-6 h-6 text-amber-500" />
                        Performance & Bonus
                    </h1>
                    <p className="text-muted-foreground">
                        Suivi des performances et calcul des bonus employés
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="week">Cette semaine</SelectItem>
                            <SelectItem value="month">Ce mois</SelectItem>
                            <SelectItem value="quarter">Ce trimestre</SelectItem>
                        </SelectContent>
                    </Select>
                    
                    {canManageBonus && (
                        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Paramètres
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Paramètres de bonus</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div>
                                        <Label>Taux de bonus sur ventes (%)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="0.5"
                                            value={bonusSettings.sales_bonus_rate}
                                            onChange={(e) => setBonusSettings({
                                                ...bonusSettings, 
                                                sales_bonus_rate: parseFloat(e.target.value)
                                            })}
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Pourcentage du CA généré
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Bonus assiduité parfaite (FCFA)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={bonusSettings.attendance_bonus}
                                            onChange={(e) => setBonusSettings({
                                                ...bonusSettings, 
                                                attendance_bonus: parseInt(e.target.value)
                                            })}
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Montant fixe pour 100% de présence
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Seuil de performance minimum (%)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={bonusSettings.performance_threshold}
                                            onChange={(e) => setBonusSettings({
                                                ...bonusSettings, 
                                                performance_threshold: parseInt(e.target.value)
                                            })}
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Score minimum pour recevoir le bonus complet
                                        </p>
                                    </div>
                                    <Button 
                                        onClick={() => {
                                            toast.success('Paramètres enregistrés');
                                            setSettingsDialogOpen(false);
                                        }}
                                        className="w-full"
                                    >
                                        Enregistrer
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Score moyen</CardDescription>
                        <CardTitle className="text-2xl flex items-center gap-2">
                            <Star className="w-5 h-5 text-amber-500" />
                            {performances.length > 0 
                                ? Math.round(performances.reduce((sum, p) => sum + (p.performance_score || 0), 0) / performances.length)
                                : 0}%
                        </CardTitle>
                    </CardHeader>
                </Card>
                
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>CA Total Équipe</CardDescription>
                        <CardTitle className="text-2xl flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-green-600" />
                            {performances.reduce((sum, p) => sum + (p.total_sales || 0), 0).toLocaleString()} CFA
                        </CardTitle>
                    </CardHeader>
                </Card>
                
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Bonus Total Prévu</CardDescription>
                        <CardTitle className="text-2xl flex items-center gap-2">
                            <Gift className="w-5 h-5 text-purple-600" />
                            {performances.reduce((sum, p) => sum + calculateBonus(p), 0).toLocaleString()} CFA
                        </CardTitle>
                    </CardHeader>
                </Card>
                
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Employés éligibles</CardDescription>
                        <CardTitle className="text-2xl flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600" />
                            {performances.filter(p => p.performance_score >= bonusSettings.performance_threshold).length}
                            <span className="text-sm font-normal text-muted-foreground">
                                / {performances.length}
                            </span>
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>
            
            {/* Performance Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        Classement des performances
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
                        </div>
                    ) : performances.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucune donnée de performance pour cette période</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {performances
                                .sort((a, b) => (b.performance_score || 0) - (a.performance_score || 0))
                                .map((perf, index) => {
                                    const badge = getPerformanceBadge(perf.performance_score || 0);
                                    const bonus = calculateBonus(perf);
                                    
                                    return (
                                        <div 
                                            key={perf.user_id}
                                            className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                        index === 0 ? 'bg-amber-500 text-white' :
                                                        index === 1 ? 'bg-gray-400 text-white' :
                                                        index === 2 ? 'bg-amber-700 text-white' :
                                                        'bg-gray-200 text-gray-600'
                                                    }`}>
                                                        {index < 3 ? (
                                                            <Trophy className="w-4 h-4" />
                                                        ) : (
                                                            <span className="text-sm font-bold">{index + 1}</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{perf.user_name}</p>
                                                        <p className="text-sm text-muted-foreground">{perf.role}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <Badge className={badge.color}>{badge.label}</Badge>
                                                    <div className="text-right">
                                                        <p className={`text-lg font-bold ${getPerformanceColor(perf.performance_score || 0)}`}>
                                                            {perf.performance_score || 0}%
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">Score</p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-4 gap-4 text-sm">
                                                <div>
                                                    <p className="text-muted-foreground">CA Généré</p>
                                                    <p className="font-medium">{(perf.total_sales || 0).toLocaleString()} CFA</p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Commandes</p>
                                                    <p className="font-medium">{perf.orders_count || 0}</p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Assiduité</p>
                                                    <p className="font-medium">{perf.attendance_rate || 0}%</p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Bonus calculé</p>
                                                    <p className={`font-bold ${bonus > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {bonus.toLocaleString()} CFA
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="mt-3">
                                                <Progress 
                                                    value={perf.performance_score || 0} 
                                                    className="h-2"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default EmployeePerformancePage;
